"use client";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useQuery } from "@tanstack/react-query";
import {
  BookOpen,
  Building2,
  Edit2,
  Inbox,
  Layers,
  Loader2,
  RefreshCw,
  Search,
  Users,
} from "lucide-react";
import { useState } from "react";

// --- Interfaces ---
// Menyesuaikan dengan join tabel master_periode_kelas & master_jurusan
interface PeriodeKelasJurusan {
  master_periode_kelas_id: number;
  kode_unit: string; // Relasi ke kode_jurusan
  nama_jurusan: string;
  jumlah_mhs: number;
  kelas: number;
}

// Menyesuaikan dengan join tabel master_periode_kelas & master_fakultas
interface PeriodeKelasFakultas {
  master_periode_kelas_id: number;
  kode_unit: string; // Relasi ke kode_fakultas
  nama_fakultas: string;
  jumlah_mhs: number;
  kelas: number;
}

interface ClusterUnit {
  id: number;
  kode: string;
  nama_unit: string;
  persentase: number;
}

// --- Mock API Fetchers ---
const fetchClusterJurusan = async (): Promise<PeriodeKelasJurusan[]> => {
  return new Promise((resolve) => {
    setTimeout(() => {
      resolve([
        {
          master_periode_kelas_id: 1,
          kode_unit: "JUR-TI",
          nama_jurusan: "Teknik Informatika",
          jumlah_mhs: 450,
          kelas: 12,
        },
        {
          master_periode_kelas_id: 2,
          kode_unit: "JUR-SI",
          nama_jurusan: "Sistem Informasi",
          jumlah_mhs: 320,
          kelas: 8,
        },
        {
          master_periode_kelas_id: 3,
          kode_unit: "JUR-AK",
          nama_jurusan: "Akuntansi",
          jumlah_mhs: 510,
          kelas: 15,
        },
        {
          master_periode_kelas_id: 4,
          kode_unit: "JUR-MN",
          nama_jurusan: "Manajemen",
          jumlah_mhs: 600,
          kelas: 18,
        },
      ]);
    }, 800);
  });
};

const fetchClusterFakultas = async (): Promise<PeriodeKelasFakultas[]> => {
  return new Promise((resolve) => {
    setTimeout(() => {
      resolve([
        {
          master_periode_kelas_id: 5,
          kode_unit: "FTK",
          nama_fakultas: "Fakultas Teknik dan Kejuruan",
          jumlah_mhs: 1250,
          kelas: 45,
        },
        {
          master_periode_kelas_id: 6,
          kode_unit: "FEB",
          nama_fakultas: "Fakultas Ekonomi dan Bisnis",
          jumlah_mhs: 1800,
          kelas: 52,
        },
        {
          master_periode_kelas_id: 7,
          kode_unit: "FIP",
          nama_fakultas: "Fakultas Ilmu Pendidikan",
          jumlah_mhs: 950,
          kelas: 38,
        },
      ]);
    }, 800);
  });
};

const fetchClusterUnit = async (): Promise<ClusterUnit[]> => {
  return new Promise((resolve) => {
    setTimeout(() => {
      resolve([
        {
          id: 1,
          kode: "viii",
          nama_unit:
            "Unit Penunjang Akademik Teknologi Informasi dan Komunikasi",
          persentase: 150,
        },
        {
          id: 2,
          kode: "xii",
          nama_unit: "Unit Penunjang Akademik Layanan Bimbingan Konseling",
          persentase: 85,
        },
        {
          id: 3,
          kode: "ix",
          nama_unit: "Unit Penunjang Akademik Bahasa",
          persentase: 85,
        },
        {
          id: 4,
          kode: "x",
          nama_unit: "Unit Penunjang Akademik Laboraturium Pendidikan Terpadu",
          persentase: 85,
        },
        {
          id: 5,
          kode: "xi",
          nama_unit:
            "Unit Penunjang Akademik Pengembangan Karier dan Kewirausahaan",
          persentase: 85,
        },
      ]);
    }, 800);
  });
};

export default function PeriodeClusterPage() {
  const [searchQuery, setSearchQuery] = useState("");
  const [activeTab, setActiveTab] = useState("jurusan");
  const [isSyncing, setIsSyncing] = useState(false);

  // --- React Query ---
  const { data: dataJurusan, isLoading: loadJurusan } = useQuery({
    queryKey: ["periode-kelas-jurusan"],
    queryFn: fetchClusterJurusan,
  });

  const { data: dataFakultas, isLoading: loadFakultas } = useQuery({
    queryKey: ["periode-kelas-fakultas"],
    queryFn: fetchClusterFakultas,
  });

  const { data: dataUnit, isLoading: loadUnit } = useQuery({
    queryKey: ["cluster-unit"],
    queryFn: fetchClusterUnit,
  });

  // --- Filter Logic ---
  const filteredJurusan = dataJurusan?.filter(
    (item) =>
      item.nama_jurusan.toLowerCase().includes(searchQuery.toLowerCase()) ||
      item.kode_unit.toLowerCase().includes(searchQuery.toLowerCase()),
  );

  const filteredFakultas = dataFakultas?.filter(
    (item) =>
      item.nama_fakultas.toLowerCase().includes(searchQuery.toLowerCase()) ||
      item.kode_unit.toLowerCase().includes(searchQuery.toLowerCase()),
  );

  const filteredUnit = dataUnit?.filter(
    (item) =>
      item.nama_unit.toLowerCase().includes(searchQuery.toLowerCase()) ||
      item.kode.toLowerCase().includes(searchQuery.toLowerCase()),
  );

  // --- Handlers ---
  const handleSync = () => {
    setIsSyncing(true);
    // Simulasi proses sinkronisasi data dari PDDIKTI / Sistem Akademik
    setTimeout(() => {
      setIsSyncing(false);
      // Di sini Anda bisa me-trigger queryClient.invalidateQueries() untuk merefresh data
    }, 1500);
  };

  // --- Helper UI ---
  const EmptyState = ({
    message,
    colSpan,
  }: {
    message: string;
    colSpan: number;
  }) => (
    <TableRow>
      <TableCell colSpan={colSpan} className="h-64 text-center">
        <div className="flex flex-col items-center justify-center text-slate-500 space-y-3">
          <div className="bg-slate-50 p-4 rounded-full border border-slate-100">
            <Inbox className="w-8 h-8 text-slate-300" />
          </div>
          <p className="text-sm text-slate-500">
            {message} <br />
            <span className="font-semibold text-slate-700">
              "{searchQuery}"
            </span>
          </p>
        </div>
      </TableCell>
    </TableRow>
  );

  const LoadingState = ({ colSpan }: { colSpan: number }) => (
    <TableRow>
      <TableCell colSpan={colSpan} className="h-64 text-center">
        <div className="flex flex-col items-center justify-center text-slate-400 space-y-3">
          <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
          <p className="text-sm">Memuat data cluster...</p>
        </div>
      </TableCell>
    </TableRow>
  );

  return (
    <div>
      <Card className="shadow-sm border-slate-200 overflow-hidden">
        <CardHeader className="bg-slate-50/80 border-b border-slate-100 pb-5">
          <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
            <div className="space-y-1.5 flex flex-row items-center gap-3">
              <div className="p-2.5 bg-blue-100/50 rounded-lg border border-blue-200/50 hidden sm:block">
                <Layers className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <CardTitle className="text-xl md:text-2xl font-bold tracking-tight text-slate-900">
                  Cluster & Pengelompokan Kelas
                </CardTitle>
                <CardDescription className="text-slate-500 text-sm mt-1">
                  Atur jumlah kelas dan mahasiswa berdasarkan Jurusan, Fakultas,
                  dan Unit.
                </CardDescription>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <div className="relative w-full sm:w-72">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  type="text"
                  placeholder="Cari nama atau kode..."
                  className="pl-9 bg-white border-slate-200 focus-visible:ring-blue-500 shadow-sm"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>

              {/* Tombol Sinkronisasi Muncul hanya di Tab Jurusan & Fakultas */}
              {activeTab !== "unit" && (
                <Button
                  onClick={handleSync}
                  disabled={isSyncing}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white gap-2 shadow-sm whitespace-nowrap"
                >
                  <RefreshCw
                    className={`w-4 h-4 ${isSyncing ? "animate-spin" : ""}`}
                  />
                  <span className="hidden sm:inline">Sinkronisasi Mhs</span>
                  <span className="sm:hidden">Sync</span>
                </Button>
              )}
            </div>
          </div>
        </CardHeader>

        <CardContent className="p-4 sm:p-6">
          <Tabs
            value={activeTab}
            onValueChange={setActiveTab}
            className="w-full"
          >
            <TabsList className="grid w-full sm:w-100 grid-cols-3 mb-6 bg-slate-100/80 p-1">
              <TabsTrigger
                value="jurusan"
                className="text-xs sm:text-sm data-[state=active]:bg-white data-[state=active]:shadow-sm"
              >
                Jurusan
              </TabsTrigger>
              <TabsTrigger
                value="fakultas"
                className="text-xs sm:text-sm data-[state=active]:bg-white data-[state=active]:shadow-sm"
              >
                Fakultas
              </TabsTrigger>
              <TabsTrigger
                value="unit"
                className="text-xs sm:text-sm data-[state=active]:bg-white data-[state=active]:shadow-sm"
              >
                Unit
              </TabsTrigger>
            </TabsList>

            {/* --- TAB CONTENT: JURUSAN --- */}
            <TabsContent value="jurusan" className="mt-0 outline-none">
              <div className="rounded-md border border-slate-200 overflow-hidden">
                <Table>
                  <TableHeader className="bg-slate-50">
                    <TableRow>
                      <TableHead className="w-16 text-center">#</TableHead>
                      <TableHead className="w-32">Kode Jurusan</TableHead>
                      <TableHead>Nama Jurusan</TableHead>
                      <TableHead className="text-center w-40">
                        <Users className="w-4 h-4 inline-block mr-1.5 mb-0.5 text-slate-400" />
                        Jml. Mahasiswa
                      </TableHead>
                      <TableHead className="text-center w-32 pr-6">
                        <BookOpen className="w-4 h-4 inline-block mr-1.5 mb-0.5 text-slate-400" />
                        Kelas
                      </TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {loadJurusan ? (
                      <LoadingState colSpan={5} />
                    ) : filteredJurusan?.length ? (
                      filteredJurusan.map((item, index) => (
                        <TableRow
                          key={item.master_periode_kelas_id}
                          className="hover:bg-slate-50"
                        >
                          <TableCell className="text-center text-slate-500 font-medium">
                            {index + 1}
                          </TableCell>
                          <TableCell className="py-3">
                            <Badge
                              variant="outline"
                              className="font-mono text-xs bg-white text-slate-600 border-slate-300"
                            >
                              {item.kode_unit}
                            </Badge>
                          </TableCell>
                          <TableCell className="font-medium text-slate-900 py-3">
                            {item.nama_jurusan}
                          </TableCell>
                          <TableCell className="text-center tabular-nums py-3">
                            {item.jumlah_mhs}
                          </TableCell>
                          <TableCell className="text-center pr-6 py-3">
                            <Badge className="bg-blue-50 text-blue-700 hover:bg-blue-50 border border-blue-200 shadow-none">
                              {item.kelas}
                            </Badge>
                          </TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <EmptyState
                        message="Tidak ada data jurusan yang cocok."
                        colSpan={5}
                      />
                    )}
                  </TableBody>
                </Table>
              </div>
            </TabsContent>

            {/* --- TAB CONTENT: FAKULTAS --- */}
            <TabsContent value="fakultas" className="mt-0 outline-none">
              <div className="rounded-md border border-slate-200 overflow-hidden">
                <Table>
                  <TableHeader className="bg-slate-50">
                    <TableRow>
                      <TableHead className="w-16 text-center">#</TableHead>
                      <TableHead className="w-32">Kode Fakultas</TableHead>
                      <TableHead>Nama Fakultas</TableHead>
                      <TableHead className="text-center w-40">
                        <Users className="w-4 h-4 inline-block mr-1.5 mb-0.5 text-slate-400" />
                        Jml. Mahasiswa
                      </TableHead>
                      <TableHead className="text-center w-32 pr-6">
                        <Building2 className="w-4 h-4 inline-block mr-1.5 mb-0.5 text-slate-400" />
                        Kelas
                      </TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {loadFakultas ? (
                      <LoadingState colSpan={5} />
                    ) : filteredFakultas?.length ? (
                      filteredFakultas.map((item, index) => (
                        <TableRow
                          key={item.master_periode_kelas_id}
                          className="hover:bg-slate-50"
                        >
                          <TableCell className="text-center text-slate-500 font-medium">
                            {index + 1}
                          </TableCell>
                          <TableCell className="py-3">
                            <Badge
                              variant="outline"
                              className="font-mono text-xs bg-white text-slate-600 border-slate-300"
                            >
                              {item.kode_unit}
                            </Badge>
                          </TableCell>
                          <TableCell className="font-medium text-slate-900 py-3">
                            {item.nama_fakultas}
                          </TableCell>
                          <TableCell className="text-center tabular-nums py-3">
                            {item.jumlah_mhs}
                          </TableCell>
                          <TableCell className="text-center pr-6 py-3">
                            <Badge className="bg-indigo-50 text-indigo-700 hover:bg-indigo-50 border border-indigo-200 shadow-none">
                              {item.kelas}
                            </Badge>
                          </TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <EmptyState
                        message="Tidak ada data fakultas yang cocok."
                        colSpan={5}
                      />
                    )}
                  </TableBody>
                </Table>
              </div>
            </TabsContent>

            {/* --- TAB CONTENT: UNIT (Dengan Aksi Edit) --- */}
            <TabsContent value="unit" className="mt-0 outline-none">
              <div className="rounded-md border border-slate-200 overflow-hidden">
                <Table>
                  <TableHeader className="bg-slate-50">
                    <TableRow>
                      <TableHead className="w-16 text-center">#</TableHead>
                      <TableHead className="w-28">Kode Unit</TableHead>
                      <TableHead>Nama Unit Penunjang</TableHead>
                      <TableHead className="text-center w-36">
                        Persentase
                      </TableHead>
                      <TableHead className="text-right pr-6 w-24">
                        Aksi
                      </TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {loadUnit ? (
                      <LoadingState colSpan={5} />
                    ) : filteredUnit?.length ? (
                      filteredUnit.map((item, index) => (
                        <TableRow
                          key={item.id}
                          className="hover:bg-slate-50 group"
                        >
                          <TableCell className="text-center text-slate-500 font-medium">
                            {index + 1}
                          </TableCell>
                          <TableCell className="py-4">
                            <Badge
                              variant="secondary"
                              className="font-mono text-xs uppercase bg-slate-100 text-slate-600 border-slate-200"
                            >
                              {item.kode}
                            </Badge>
                          </TableCell>
                          <TableCell className="font-medium text-slate-800 py-4 leading-snug">
                            {item.nama_unit}
                          </TableCell>
                          <TableCell className="text-center py-4">
                            <Badge
                              className={`${item.persentase > 100 ? "bg-emerald-100 text-emerald-800 border-emerald-200" : "bg-amber-50 text-amber-700 border-amber-200"} shadow-none border`}
                            >
                              {item.persentase}%
                            </Badge>
                          </TableCell>
                          <TableCell className="text-right pr-6 py-4">
                            <Button
                              size="sm"
                              variant="ghost"
                              className="h-8 w-8 p-0 text-slate-400 hover:text-emerald-600 opacity-0 group-hover:opacity-100"
                            >
                              <Edit2 className="w-4 h-4" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <EmptyState
                        message="Tidak ada data unit yang cocok."
                        colSpan={5}
                      />
                    )}
                  </TableBody>
                </Table>
              </div>
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>
    </div>
  );
}
